// Audio graph shared by sound effects and the ride ambience: one lazily created context, two buses into a master
// gain, a gentle compressor so stacked effects never clip, and a short synthetic reverb (no audio files).
import { state } from '../data/store.js';

export interface Bus { ac: AudioContext; master: GainNode; sfx: GainNode; amb: GainNode; verb: GainNode; noise: AudioBuffer }

let bus: Bus | null = null;
let failed = false;

/** Master volume from the settings, bounded: a hand-edited localStorage value must not blow the speakers. */
export const masterVolume = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.7);

/** Exponential decay of filtered noise: a small-room impulse, darker in its tail like a real space. */
function impulse(ac: AudioContext, seconds: number): AudioBuffer {
  const n = Math.floor(ac.sampleRate * seconds), b = ac.createBuffer(2, n, ac.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const k = i / n, a = 0.35 + 0.6 * k; // the low-pass closes over time
      lp += a * ((Math.random() * 2 - 1) - lp);
      d[i] = lp * Math.pow(1 - k, 3);
    }
  }
  return b;
}

function build(): Bus {
  const ac = new AudioContext();
  const master = ac.createGain(), comp = ac.createDynamicsCompressor();
  comp.threshold.value = -14; comp.knee.value = 12; comp.ratio.value = 4; comp.attack.value = 0.004; comp.release.value = 0.2;
  master.connect(comp).connect(ac.destination);
  const sfx = ac.createGain(), amb = ac.createGain(), verb = ac.createGain(), conv = ac.createConvolver();
  sfx.gain.value = 0.9; amb.gain.value = 1; verb.gain.value = 0.18;
  conv.buffer = impulse(ac, 1.1);
  sfx.connect(master); amb.connect(master);
  verb.connect(conv).connect(master);
  // two seconds of white noise, shared by every noisy voice instead of a fresh buffer per sound
  const noise = ac.createBuffer(1, ac.sampleRate * 2, ac.sampleRate), d = noise.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const b: Bus = { ac, master, sfx, amb, verb, noise };
  document.addEventListener('visibilitychange', () => { if (document.hidden) ac.suspend().catch(() => {}); });
  return b;
}

/** The audio bus, created on first use (after a user gesture) and resumed if the tab suspended it. Null without WebAudio. */
export function audio(): Bus | null {
  if (failed) return null;
  if (!bus) {
    try { bus = build(); } catch { failed = true; return null; }
  }
  if (bus.ac.state === 'suspended' && !document.hidden) bus.ac.resume().catch(() => {});
  bus.master.gain.setTargetAtTime(masterVolume(state.settings.volume), bus.ac.currentTime, 0.02);
  return bus;
}
